import * as React from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import ptBR from "date-fns/locale/pt-BR";
import { Calendar, AlertCircle } from "lucide-react";

import { cn } from "../lib/utils";
import { Label } from "./ui/label";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("ptBR", ptBR);

interface DateHourPickerProps {
  title?: string;
  error?: string;
  required?: boolean;
  name?: string;
  className?: string;
  onChange?: (date: Date | null) => void;
  [key: string]: any; // Para outras props do DatePicker
}

const DateHourPicker = React.forwardRef<any, DateHourPickerProps>(
  ({ title, error, required, name, className, onChange, ...props }, ref) => {
    const pickerId = name || `datepicker-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <div className="space-y-2">
        {title && (
          <Label
            htmlFor={pickerId}
            className={cn(
              "text-sm font-medium text-muted-foreground",
              error && "text-red-600 dark:text-red-400"
            )}
          >
            {title}
            {required && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
          </Label>
        )}
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary pointer-events-none z-10" />
          <DatePicker
            id={pickerId}
            name={name}
            ref={ref}
            locale="ptBR"
            dateFormat="dd/MM/yyyy HH:mm"
            showTimeSelect
            timeFormat="HH:mm"
            timeIntervals={15}
            timeCaption="Hora"
            onChange={onChange || (() => {})}
            className={cn(
              "flex h-10 w-full rounded-md border border-border bg-background text-foreground pl-10 pr-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
              error && "border-red-300 dark:border-red-800 focus:ring-red-500",
              className
            )}
            wrapperClassName="w-full"
            {...props}
          />
        </div>
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </div>
        )}
      </div>
    );
  }
);

DateHourPicker.displayName = "DateHourPicker";

export { DateHourPicker };
